import { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { DataTable } from "@/components/ui/data-table/index";
import { Column } from "@/components/ui/data-table/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CompanyWithFootprint } from "../../types/assimagra/assimagra-metrics.types";
import { formatNumber } from "../../utils/numberFormatter";
import { TableHeader } from "@/components/ui/table-header";

interface CompaniesDataTableProps {
  companies: CompanyWithFootprint[];

}

export const CompaniesDataTable = ({ companies }: CompaniesDataTableProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const getActivityTypeBadge = (activityType: string) => {
    // Normalize and validate activity type
    const normalizedActivityType = activityType?.toLowerCase()?.trim();

    switch (normalizedActivityType) {
      case "extraction":
      case "extração":
        return (
          <Badge variant="secondary" className="bg-blue-50 text-blue-700">
            {t("assimagraDashboard.companiesTable.activityTypes.extraction")}
          </Badge>
        );
      case "transformation":
      case "transformação":
        return (
          <Badge variant="secondary" className="bg-green-50 text-green-700">
            {t("assimagraDashboard.companiesTable.activityTypes.transformation")}
          </Badge>
        );
      case "extraction_and_transformation":
      case "extração_e_transformação":
      case "both":
        return (
          <Badge variant="secondary" className="bg-purple-50 text-purple-700">
            {t("assimagraDashboard.companiesTable.activityTypes.both")}
          </Badge>
        );
      default:
        // Handle invalid/corrupted activity types by showing "Unknown" with a warning style
        console.warn(`Invalid activity type detected: ${activityType}`);
        return (
          <Badge variant="secondary" className="bg-gray-50 text-gray-700">
            {t("assimagraDashboard.companiesTable.activityTypes.unknown")}
          </Badge>
        );
    }
  };

  // Filter companies based on search term
  const filteredCompanies = companies.filter(
    (company) =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.associate_number && company.associate_number.toString().includes(searchTerm))
  );

  const columns: Column<CompanyWithFootprint>[] = [
    {
      header: t("assimagraDashboard.companiesTable.columns.associateNumber"),
      accessorKey: "associate_number",
      sortable: true,
      className: "w-[8%] min-w-[80px] text-center",
      cell: (_: string, row) => <span className="font-mono text-sm">{row.associate_number || "-"}</span>,
    },
    {
      header: t("assimagraDashboard.companiesTable.columns.companyName"),
      accessorKey: "name",
      sortable: true,
      className: "w-[40%] min-w-[250px]",
      cell: (_: string, row) => {
        return (
          <div>
            <div className="font-medium text-gray-900">{row.name}</div>
            <div className="text-sm text-gray-500">
              {row.total_products_count > 0 ? (
                `${row.total_products_count} ${t(`assimagraDashboard.companiesTable.productCount.${row.total_products_count > 1 ? "plural" : "singular"}`)} ${t(`assimagraDashboard.companiesTable.calculated.${row.total_products_count > 1 ? "plural" : "singular"}`)}`
              ) : (
                t("assimagraDashboard.companiesTable.noProductsCalculated")
              )}
            </div>
          </div>
        );
      },
    },
    {
      header: t("assimagraDashboard.companiesTable.columns.activityType"),
      accessorKey: "activity_type",
      sortable: true,
      className: "w-[15%] min-w-[120px]",
      cell: (_: string, row) => getActivityTypeBadge(row.activity_type),
    },
    {
      header: t("assimagraDashboard.companiesTable.columns.carbonFootprint"),
      accessorKey: "latest_carbon_footprint",
      sortable: true,
      className: "w-[18%] min-w-[140px] text-right",
      cell: (_: string, row) => (
        <div className="text-right">
          <div className="font-medium">{formatNumber(row.latest_carbon_footprint)}</div>
          <div className="text-xs text-gray-500">{t("assimagraDashboard.companiesTable.units.carbon")}</div>
        </div>
      ),
    },
    {
      header: t("assimagraDashboard.companiesTable.columns.hydricFootprint"),
      accessorKey: "latest_hydric_footprint",
      sortable: true,
      className: "w-[14%] min-w-[120px] text-right",
      cell: (_: string, row) => (
        <div className="text-right">
          <div className="font-medium">{formatNumber(row.latest_hydric_footprint)}</div>
          <div className="text-xs text-gray-500">{t("assimagraDashboard.companiesTable.units.hydric")}</div>
        </div>
      ),
    },
    {
      header: t("assimagraDashboard.companiesTable.columns.actions"),
      accessorKey: "id",
      sortable: false,
      className: "w-[5%] min-w-[80px] text-center",
      cell: (_: string, row) => (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/companies/${row.id}/dashboard`)}
            className="h-8 w-8 p-0"
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t("assimagraDashboard.companiesTable.title")}</h3>
          <p className="text-sm text-gray-500">
            {t("assimagraDashboard.companiesTable.subtitle", {
              count: companies.length,
            })}
          </p>
        </div>
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("assimagraDashboard.companiesTable.search")}
      />

      {filteredCompanies.length > 0 ? (
        <DataTable data={filteredCompanies} columns={columns} pageSize={10} className="w-full" minWidth="auto" />
      ) : (
        <div className="flex justify-center items-center p-8 bg-white rounded-xl shadow-sm">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {t("assimagraDashboard.companiesTable.noCompanies.title")}
            </h3>
            <p className="text-gray-500">{t("assimagraDashboard.companiesTable.noCompanies.description")}</p>
          </div>
        </div>
      )}
    </div>
  );
};
